import { ref } from 'vue';
import ApiService from "@/_services/api.service";
import swal from "@/_helpers/swal";
import {UserBasicRepresentation} from "@/models/user/UserBasicRepresentation";
import {IFormkitDropDown} from "@/models/formkit/dropDown/IFormkitDropDown";

export async function getUser(uuid: string): Promise<object|null> {
    try {
        const { data } = await ApiService.get("users/" + uuid);
        
        return data.data;
    } catch (error) {
        swal.error();
        return null;
    }
}

export async function getUsers(
    itemsPerPage: number,
    page: number,
    status: string,
    enabled: boolean,
    criteria?: null|string,
    sort?: { fieldLabel: string; fieldValue: string; }
): Promise<[boolean, number, UserBasicRepresentation[]]>
{
    const errorMsg = 'Une erreur est survenue au chargement des utilisateurs.';
    const isLoading = ref<boolean>(true);
    const totalRecords = ref<number>(0);
    const users = Array<UserBasicRepresentation>();
    const tableData = ref<Array<UserBasicRepresentation>>(users);
    let params = {
        itemsPerPage: itemsPerPage,
        page: page,
        status: status,
        enabled: enabled,
        search: criteria,
    };

    if (criteria !== "") {
        params = { ...params, search: criteria?.trim() };
    }

    if (sort !== undefined) {
        const { fieldValue, fieldLabel } = sort;
        params = {
            ...params,
            [fieldLabel]: fieldValue,
        };
    }

    try {
        const { data } = await ApiService.get('admin/users', {
            params: params,
            headers: { Accept: 'application/ld+json' },
        });

        if (!data || !data.data) {
            throw new Error(errorMsg);
        }

        totalRecords.value = data.data['hydra:totalItems'];
        data.data["hydra:member"].forEach((item: any) => {
            if (item) {
                tableData.value.push({
                    uuid: item.uuid,
                    email: item.email || '',
                    firstname: item.firstname,
                    lastname: item.lastname,
                    userBlackList: item.userBlackList ? item.userBlackList.uuid : null,
                });
            }
        });
    } catch (error) {
        console.error(error);
        swal.error(errorMsg);
    }

    isLoading.value = false;

    return [isLoading.value, totalRecords.value, tableData.value];
}

export async function getPaginatedUsersWithoutMatriculeByCompanyForDropDown(
    companyUuid: string,
    itemsPerPage: number,
    page: number,
    criteria?: string,
    active?: boolean,
): Promise<[number, IFormkitDropDown[]]> {
    const totalCompaniesRecords = ref<number>(0);
    const companies = Array<IFormkitDropDown>()
    const usersForDropDown = ref<Array<IFormkitDropDown>>(companies);
    
    interface IParams { itemsPerPage: number; page: number; search?: string; active?: boolean; }

    let params: IParams = {
        itemsPerPage: itemsPerPage,
        page: page,
    };

    if (criteria !== undefined && criteria !== '') {
        params = { ...params, search: criteria.trim() };
    }

    if (active !== undefined) {
        params = { ...params, active: active };
    }

    try {
        const { data } = await ApiService.get(`/admin/company-users-without-matricule/${companyUuid}`, {
            headers: { Accept: 'application/ld+json' },
            params: params,
        });

        totalCompaniesRecords.value = data.data['hydra:totalItems'];
        const transformedData: IFormkitDropDown[] = data.data['hydra:member']
            .map((item: { uuid: string, fullname: string }) => ({
                label: item.fullname,
                value: item.uuid,
            }));
        usersForDropDown.value.push(...transformedData);
    } catch (error) {
        swal.error();
    }

    return [totalCompaniesRecords.value, usersForDropDown.value];
}